import { MutableRefObject, useEffect, useRef, useState } from "react"
import WebViewer from "@pdftron/webviewer"
import type { WebViewerInstance } from "@pdftron/webviewer"
import { useViewerStore } from "store/viewer.store"
import { useCurrentUserStore } from "store/user.store"
import { useDocumentLoadStore } from "store/documentLoad.store"
import { fetchDocumentBlob, resolveDocumentIdentity } from "components/ui/customWebviewer/core/documentBlobCache"

const licenseKey = import.meta.env.VITE_WEBVIEWER_KEY

// Matches the quotation editor's threshold: below this the wait is not worth covering the screen.
const DOCUMENT_LOAD_ANNOUNCE_DELAY_MS = 250

// A Blob carries no name, and without one Apryse shows the document as untitled.
const resolveDocumentFilename = (fileIdentity: string | undefined): string => {
	const lastSegment = fileIdentity?.split("/").pop()
	if (!lastSegment) return "document.pdf"
	try {
		return decodeURIComponent(lastSegment)
	} catch {
		return lastSegment
	}
}

type Props = {
	fileLink: string | undefined
	previewRef: MutableRefObject<null>
	isModalOpen: boolean
}

export const useWebviewer = ({ fileLink, previewRef, isModalOpen }: Props) => {
	const instanceRef = useRef<WebViewerInstance | null>(null)
	// We register this preview's instance via setPreviewInstance, which publishes ONLY
	// the navigation service (the AI Agent's page-reference navigation reads it) and the
	// store instance (DocumentModal's resize handling reads it). It intentionally does
	// NOT register AnnotationService — that singleton belongs to the custom quotation
	// editor; pointing it at the read-only preview hijacks it (promoteUserToAdmin, cached
	// annotations re-rendering on the preview, editor losing listeners on reopen). See
	// PR #1118, which over-corrected by dropping registration entirely and so broke the
	// AI Agent "PDF viewer not initialized" navigation.
	const { setPreviewInstance, clearPreviewInstance, openFeedbackPanel } = useViewerStore()
	const loadingTaskRef = useRef<Promise<unknown> | null>(null)
	// Scopes the cached bytes to whoever is signed in — see documentBlobCache.buildDocumentCacheKey.
	const currentUserId = useCurrentUserStore((state) => state.currentUser.id)
	const [isInitialized, setIsInitialized] = useState(false)

	// Initialize WebViewer once. WebViewer() is async, so instanceRef stays null until
	// it resolves. The loadingTaskRef guard prevents a second WebViewer() call while
	// that init is still in flight — without it, a fileLink that arrives at modal-open
	// time (as in the Documents tab, where the download URL is fetched on click) re-runs
	// this effect before instanceRef is set and spins up a duplicate viewer, leaving the
	// document area blank. The Quotations tab prefetches the URL so fileLink is stable
	// and never hit this race.
	useEffect(() => {
		if (!previewRef.current || instanceRef.current || loadingTaskRef.current) {
			return
		}

		loadingTaskRef.current = WebViewer(
			{
				licenseKey: licenseKey,
				path: "/webviewer",
				// No `initialDoc`: the load effect below is the single loader, so the bytes go through
				// the shared document cache instead of Apryse fetching the URL itself. Measured before
				// the change: `fileLink` is undefined at construction here anyway (the Documents tab
				// resolves the presigned URL on click), so this was already a no-op and its removal
				// costs nothing — it just makes the single-loader rule explicit, as in the quotation
				// editor.
				css: "/webviewer.css",
				disabledElements: [
					"toolbarGroup-Annotate",
					"toolbarGroup-Insert",
					"toolbarGroup-Edit",
					"toolbarGroup-FillAndSign",
					"toolbarGroup-Forms",
					"notesPanelToggle",
				],
				// Production stability options
				enableOptimizedWorkers: true,
				fullAPI: false,
				useDownloader: false,
				// disableVirtualDisplayMode: true, // Disable virtual display mode
			},
			previewRef.current,
		)
			.then((viewerInstance: WebViewerInstance) => {
				// Expose the WebViewer instance globally so automated tests (e.g. Playwright)
				// can verify annotation selection/highlight after navigation. No effect on app
				// behavior — see https://docs.apryse.com/web/faq/react-global-instance
				const globalWindow = window as unknown as { __wvInstance?: WebViewerInstance }
				globalWindow.__wvInstance = viewerInstance

				instanceRef.current = viewerInstance
				// Register so the AI Agent reference navigation (ViewerNavigationService)
				// and DocumentModal's resize handling (store instance) can reach the viewer.
				setPreviewInstance(viewerInstance)

				const { UI } = viewerInstance

				const customButton = new UI.Components.CustomButton({
					title: "Share feedback",
					label: "Share feedback",
					dataElement: "feedbackButton",
					onClick: () => {
						openFeedbackPanel()
					},
				})

				const defaultHeader = UI.getModularHeader("default-top-header")
				const items = defaultHeader.getItems()
				const searchIndex = items.findIndex(
					(item: { dataElement?: string }) => item.dataElement === "searchPanelToggle",
				)

				items.splice(searchIndex > -1 ? searchIndex : items.length, 0, customButton)
				defaultHeader.setItems(items)

				setIsInitialized(true)
			})
			.catch((error: Error) => {
				console.error("WebViewer initialization failed", error)
				loadingTaskRef.current = null
			})
		// isModalOpen is a dep so opening the modal triggers init when fileLink is already
		// stable (Quotations prefetches the URL); the guard above makes the re-runs safe.
	}, [fileLink, previewRef, setPreviewInstance, openFeedbackPanel, isModalOpen])

	// Release the navigation singleton + store instance when the preview unmounts, so a
	// disposed viewer is never left registered for the next consumer. We clear on unmount
	// (not on modal close) because the instance is created once and reused across reopens —
	// the init effect's .then (where setPreviewInstance runs) does not fire again.
	useEffect(() => {
		return () => {
			clearPreviewInstance()
		}
	}, [clearPreviewInstance])

	// Load the document once the instance is ready, and reload it on modal reopen
	// (closeDocument() runs on close below). Gating on the isInitialized state — rather
	// than the instanceRef, which can't trigger a re-run — guarantees the load fires
	// even when fileLink was still undefined at construction time.
	useEffect(() => {
		if (!instanceRef.current || !fileLink || !isInitialized || !isModalOpen) return
		const { UI } = instanceRef.current
		const fileIdentity = resolveDocumentIdentity(fileLink)
		const userScope = currentUserId == null ? undefined : String(currentUserId)
		const { start, report, finish } = useDocumentLoadStore.getState()

		let cancelled = false
		let hasAnnounced = false
		// Nothing is announced for the first moments: a cache hit resolves in milliseconds, and so
		// does a small drawing on a fast link — announcing either would flash the overlay on open.
		const announceTimer = window.setTimeout(() => {
			if (cancelled) return
			hasAnnounced = true
			start()
		}, DOCUMENT_LOAD_ANNOUNCE_DELAY_MS)
		let lastPercent = -1

		fetchDocumentBlob({
			fileIdentity,
			userScope,
			url: fileLink,
			onProgress: ({ loaded, total }) => {
				if (!hasAnnounced || cancelled || total === null) return
				const fraction = Math.min(loaded / total, 1)
				const percent = Math.floor(fraction * 100)
				if (percent === lastPercent) return
				lastPercent = percent
				report(fraction)
			},
		})
			.then((blob) => {
				if (cancelled) return
				return UI.loadDocument(blob, { filename: resolveDocumentFilename(fileIdentity), extension: "pdf" })
			})
			.catch((error) => {
				if (cancelled) return
				// The preview must still open when the prefetch or the parse fails — fall back to the
				// URL, which is what this did before the cache existed.
				console.warn("Failed to prefetch the document; loading by URL instead:", error)
				UI.loadDocument(fileLink)
			})
			.finally(() => {
				window.clearTimeout(announceTimer)
				if (hasAnnounced) finish()
			})

		return () => {
			cancelled = true
			window.clearTimeout(announceTimer)
			if (hasAnnounced) finish()
		}
	}, [fileLink, isInitialized, isModalOpen, currentUserId])

	// Handle cleanup when the modal is closed
	useEffect(() => {
		if (!isModalOpen && instanceRef.current) {
			const { Core } = instanceRef.current
			if (Core && Core.documentViewer) {
				try {
					Core.documentViewer.closeDocument()
				} catch (error) {
					console.warn("Could not close document:", error)
				}
			}
		}
	}, [isModalOpen])
}
